import { GetOnlineAccountResponse } from '@iwoca/lapi-client/edge';

import { hasExtendableProvider } from './hasExtendableProvider';

export type OpenBankingStatus =
    'disconnected' | 'expired' | 'expiring' | 'connected';

type OnlineAccount = GetOnlineAccountResponse['data'][number];

export const OB_EXPIRY_THRESHOLD_DAYS = 14;

export const getOpenBankingStatus = (
    onlineAccounts: OnlineAccount[],
    expiryThresholdDays: number = OB_EXPIRY_THRESHOLD_DAYS
): OpenBankingStatus => {
    const relevantAccounts = onlineAccounts.filter(
        (account) => account.status.code !== 'connecting'
    );

    if (relevantAccounts.length === 0) {
        return 'disconnected';
    }

    const activeAccounts = relevantAccounts.filter(
        (account) => account.status.code === 'active'
    );

    const now = Date.now();
    const thresholdMs = expiryThresholdDays * 24 * 60 * 60 * 1000;

    const hasExpiring = activeAccounts.some((account) => {
        // Nothing to extend without a provider, so 'expiring' would lead
        // nowhere.
        if (!hasExtendableProvider(account) || !account.expiry_at) return false;

        const timeUntilExpiry = new Date(account.expiry_at).getTime() - now;

        return timeUntilExpiry <= thresholdMs;
    });

    if (hasExpiring) {
        return 'expiring';
    }

    if (activeAccounts.length > 0) {
        return 'connected';
    }

    const hasExpired = relevantAccounts.some(
        (account) => account.status.code === 'expired'
    );

    if (hasExpired) {
        return 'expired';
    }

    return 'disconnected';
};
